import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, BrainIcon, SparklesIcon, SunIcon, CalendarIcon, Users, ArrowRightIcon, MenuIcon, XIcon, MoonIcon } from 'lucide-react';
const HERO_MOUNTAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/wKy5wTQ9caLQgtnJUX6gAj/philipp-jW6ZpBFE9dE-unsplash.jpg';
// ═══════════════════════════════════════════════════════════
// 🔗 REGISTRACIJOS NUORODA — čia įrašykite savo anketos URL
// ═══════════════════════════════════════════════════════════
const REGISTRATION_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdN1pDWRqjo_eykjFE7VECqifWUKuHBRr2Fq-LVvCOnP6_lwg/viewform?usp=publish-editor';
export function LandingPage({
  mode,
  toggleMode
}: {
  mode: 'light' | 'dark';
  toggleMode: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialDirection, setTestimonialDirection] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.65,
      ease: 'easeOut'
    }
  };
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const accentText = 'DFB Metodika:';
  const charVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.045,
        duration: 0.4,
        ease: 'easeOut'
      }
    })
  };
  const headlineVariants = {
    hidden: {
      opacity: 0,
      y: 24,
      filter: 'blur(12px)'
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.9,
        ease: 'easeOut',
        delay: 0.3
      }
    }
  };
  const subVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        delay: 0.9
      }
    }
  };
  const ctaVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        delay: 1.4
      }
    }
  };
  // Reusable section label
  const SectionLabel = ({
    text,
    light = false
  }: {
    text: string;
    light?: boolean;
  }) => <div className="flex flex-col items-center mb-5" data-id="element-2">
      <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${light ? 'text-gold-light/70' : 'text-gold-dark'}`} data-id="element-3">
        {text}
      </p>
      <div className="w-10 h-0.5 bg-gold" data-id="element-4" />
    </div>;
  return <main className="w-full bg-sand-lightest transition-colors duration-300" data-id="element-5">
      {/* ── STICKY NAV ── */}
      <motion.nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500" style={{
      background: scrolled ? 'rgba(var(--mountain-darker), 0.96)' : 'transparent',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      boxShadow: scrolled ? '0 1px 0 rgba(255,255,255,0.06)' : 'none'
    }} initial={{
      y: -80,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut',
      delay: 2.0
    }} data-id="element-6">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-7">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 select-none" data-id="element-8">
            <span className="font-accent tracking-wide text-[#F72585] text-[32px] font-[500]" data-id="element-9">
              medituoti lengva
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-10">
            {[{
            label: 'Apie',
            href: '#apie'
          }, {
            label: 'Metodika',
            href: '#metodika'
          }, {
            label: 'Programa',
            href: '#programa'
          }, {
            label: 'Komanda',
            href: '#komanda'
          }, {
            label: 'Tvarkaraštis',
            href: '#tvarkarastis'
          }].map(link => <a key={link.label} href={link.href} className="text-white/70 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors duration-200" data-id="element-11">
                {link.label}
              </a>)}
          </div>

          {/* CTA & Dark Mode */}
          <div className="hidden md:flex items-center gap-4" data-id="element-12">
            <button onClick={toggleMode} className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Toggle dark mode" data-id="element-13">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-14" /> : <SunIcon className="w-5 h-5" data-id="element-15" />}
            </button>
            <motion.a href="https://ig.me/m/vestirna" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-colors shadow-lg" style={{
            background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
          }} data-id="element-16">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-17">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-18" />
              </svg>
              Parašyk mums
            </motion.a>
          </div>

          {/* Mobile burger & Dark Mode */}
          <div className="flex items-center gap-2 md:hidden" data-id="element-19">
            <button onClick={toggleMode} className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Toggle dark mode" data-id="element-20">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-21" /> : <SunIcon className="w-5 h-5" data-id="element-22" />}
            </button>
            <button className="text-white/80 hover:text-white p-1" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Meniu" data-id="element-23">
              {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-24" /> : <MenuIcon className="w-6 h-6" data-id="element-25" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence data-id="element-26">
          {mobileOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="md:hidden overflow-hidden" style={{
          background: 'rgba(var(--mountain-darker), 0.98)'
        }} data-id="element-27">
              <div className="px-6 py-4 flex flex-col gap-4 border-t border-white/10" data-id="element-28">
                {[{
              label: 'Apie',
              href: '#apie'
            }, {
              label: 'Metodika',
              href: '#metodika'
            }, {
              label: 'Programa',
              href: '#programa'
            }, {
              label: 'Komanda',
              href: '#komanda'
            }, {
              label: 'Tvarkaraštis',
              href: '#tvarkarastis'
            }].map(link => <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white text-base font-medium uppercase tracking-wider py-1 transition-colors" data-id="element-29">
                    {link.label}
                  </a>)}
                <a href="https://ig.me/m/vestirna" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full text-sm font-semibold text-center mt-2" style={{
              background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
            }} data-id="element-30">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-31">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-32" />
                  </svg>
                  Parašyk mums
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.nav>

      {/* ── HERO ── */}
      <section className="relative h-screen min-h-[640px] flex flex-col items-center justify-center px-4 overflow-hidden" data-id="element-33">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url('${HERO_MOUNTAIN_IMAGE}')`
      }} data-id="element-34" />

        <div className="relative z-20 max-w-4xl mx-auto text-center flex flex-col items-center" data-id="element-35">
          <motion.p className="text-sm font-accent text-gold-light mb-5 tracking-widest uppercase" initial="hidden" animate="visible" aria-label={accentText} data-id="element-36">
            {accentText.split('').filter((_, i) => i !== 0 && i !== 1 && i !== 2 && i !== 4 && i !== 5 && i !== 6 && i !== 7 && i !== 8 && i !== 9 && i !== 10 && i !== 11 && i !== 12).map((char, i) => <motion.span key={i} custom={i} variants={charVariants} style={{
            display: char === ' ' ? 'inline' : 'inline-block'
          }} data-id="element-37">
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>)}
          </motion.p>

          <motion.h1 className="text-4xl md:text-6xl lg:text-7xl mb-6 leading-tight text-[#FFFFFF] font-[600] bg-[#FFFFFF00]" style={{
          textShadow: '0 1px 3px rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.3), 0 8px 30px rgba(0,0,0,0.15)'
        }} variants={headlineVariants} initial="hidden" animate="visible" data-id="element-38">
            Tavo Dėmesys Yra Brangiausias Tavo Resursas
          </motion.h1>

          <motion.p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed font-[300]" style={{
          textShadow: '0 1px 2px rgba(0,0,0,0.5), 0 3px 10px rgba(0,0,0,0.25)'
        }} variants={subVariants} initial="hidden" animate="visible" data-id="element-39">
            Tavo energija keliauja ten, kur nukreipi savo dėmesį. Treniruok jį
            ir stebėk, kaip keičiasi tavo vidinė būsena ir produktyvumas.
          </motion.p>

          <motion.a href="#apie" variants={ctaVariants} initial="hidden" animate="visible" whileHover={{
          scale: 1.05,
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.4)'
        }} whileTap={{
          scale: 0.97
        }} className="mt-4 bg-white hover:bg-gray-100 text-gray-900 px-11 py-[15px] rounded-full text-base font-semibold shadow-2xl transition-colors inline-block" data-id="element-40">
            Sužinok Daugiau
          </motion.a>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-30 translate-y-[1px]" data-id="element-41">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" data-id="element-42">
            <path d="M0 0C240 80 480 80 720 40C960 0 1200 0 1440 40V120H0V0Z" style={{
            fill: 'rgb(var(--sand-light))'
          }} data-id="element-43" />
          </svg>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section id="apie" className="py-20 px-4 bg-sand-light" data-id="element-44">
        <motion.div className="max-w-3xl mx-auto text-center" {...fadeInUp} data-id="element-45">
          <p className="text-lg md:text-xl text-mountain-dark leading-relaxed font-[300]" data-id="element-46">
            Šis kursas pakeis tavo požiūrį į meditaciją, transformuodamas ją į
            sąmoningą dėmesio valdymą. Užuot tik bandęs sugrįžti į „čia ir
            dabar", išmoksi tikslingai nukreipti savo dėmesį taip, kad
            reguliuotum savo vidinę būseną. Būtent čia ir slepiasi tavo laisvė.
          </p>
        </motion.div>
      </section>

      {/* ── CHALLENGES ── */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-47">
        <div className="max-w-6xl mx-auto" data-id="element-48">
          <motion.div className="mb-12" {...fadeInUp} data-id="element-49">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-50">
              Visiems Pažįstami
            </p>
            <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-51" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-52">
              Kasdieniai Iššūkiai
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-53">
            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-54">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1537210249814-b9a10a161ae4?w=600&q=80')"
            }} data-id="element-55" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-56" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-57">
                01
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-58">
                <p className="text-lg font-bold text-white mb-1" data-id="element-59">
                  Nuolatinis stresas
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-60">
                  Kaip efektyviai spręsti problemas neprarandant vidinės
                  ramybės?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-61">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://cdn.magicpatterns.com/uploads/fkDNhwZwL2Fman3fp7TuCW/moritz-kindler-n6XW1P6a-_U-unsplash.jpg')"
            }} data-id="element-62" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-63" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-64">
                02
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-65">
                <p className="text-lg font-bold text-white mb-1" data-id="element-66">
                  Dėmesio blaškymasis
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-67">
                  Kaip valdyti dėmesį nuolat besikeičiančioje aplinkoje?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-68">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1669369172813-938c5aaa8685?w=600&q=80')"
            }} data-id="element-69" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-70" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-71">
                03
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-72">
                <p className="text-lg font-bold text-white mb-1" data-id="element-73">Perdegimas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-74">
                  Kaip siekti ilgalaikių tikslų neperdegant?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-75">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1516490981167-dc990a242afe?w=600&q=80')"
            }} data-id="element-76" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-77" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-78">
                04
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-79">
                <p className="text-lg font-bold text-white mb-1" data-id="element-80">Chaosas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-81">
                  Ką daryti su emocijomis sudėtingose situacijose?
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── TARP NUOLATINĖS KAITOS ── */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-82">
        <div className="max-w-6xl mx-auto" data-id="element-83">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-84">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-85">
              Paieškos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-86" />
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight" data-id="element-87">
              Tarp nuolatinės kaitos ir pusiausvyros paieškų
            </h2>
          </motion.div>

          <motion.div className="flex flex-col gap-12 items-center" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-88">
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto" data-id="element-89">
              <p className="text-[20px] text-white/80 font-[300]" data-id="element-90">
                Šiandienos pasaulis sukasi neregėtu tempu. Informacijos srautas,
                nuolatiniai reikalavimai ir pokyčiai kuria vidinę įtampą, kuri
                laikui bėgant virsta stresu, perdegimo jausmu ir prarastu ryšiu
                su savimi.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="w-full max-w-2xl mx-auto space-y-0" data-id="element-91">
              {['Kaip efektyviai spręsti problemas neprarandant vidinės ramybės?', 'Kaip giliai atsipalaiduoti ir tikrai pailsėti?', 'Kaip pasiekti gyvenime harmoniją ir pilnatvę?', 'Kaip valdyti emocijas ir dėmesį sudėtingose situacijose?'].map((question, i) => <div key={i} className="flex items-start gap-5 py-6" style={{
              borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none'
            }} data-id="element-92">
                  <span className="font-bold text-gold-light flex-shrink-0" style={{
                fontSize: '2rem',
                lineHeight: 1.1
              }} data-id="element-93">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-white/80 text-lg leading-relaxed pt-0.5" data-id="element-94">
                    {question}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT IS DFB ── */}
      <section id="metodika" className="relative overflow-hidden" style={{
      minHeight: 600
    }} data-id="element-95">
        {/* Full-width background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1920&q=80')"
      }} data-id="element-96" />
        {/* Left-to-right cinematic gradient */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to right, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.25) 70%, transparent 100%)'
      }} data-id="element-97" />

        {/* Content — left half */}
        <motion.div className="relative z-10 flex items-center" style={{
        minHeight: 600
      }} {...fadeInUp} data-id="element-98">
          <div className="max-w-7xl mx-auto w-full px-8 md:px-16 py-24" data-id="element-99">
            <div className="max-w-xl" data-id="element-100">
              {/* Italic subtitle — now above */}
              <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-101">
                Sprendimas
              </p>
              {/* Gold line */}
              <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
              background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
            }} data-id="element-102" />
              {/* Section heading */}
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight" data-id="element-103">
                Sąmoningas Būsenų Valdymas
              </h2>
              {/* Paragraphs */}
              <p className="text-white/80 mb-5 font-[300] text-[20px]" data-id="element-104">
                Diferencijuotos Funkcinės Būsenos – tai aktyvios
                savireguliacijos metodika, leidžianti sąmoningai valdyti vidines
                būsenas nepriklausomai nuo išorinės veiklos.
              </p>
              <p className="text-white/80 font-[300] text-[20px]" data-id="element-105">
                Metodika unikali tuo, kad moko ne slopinti emocijas, o jas
                diferencijuoti, sąmoningai pasirinkti ir pritaikyti.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 16 STATES ── (dark bg) */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-106">
        <div className="max-w-6xl mx-auto" data-id="element-107">
          <motion.div className="text-center mb-10" {...fadeInUp} data-id="element-108">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-109">
              Aktyvios Savireguliacijos Sistema
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-110" />
            <h3 className="text-3xl md:text-4xl font-bold text-white" data-id="element-111">
              16 Funkcinių Būsenų
            </h3>
          </motion.div>

          <motion.div className="mb-16 max-w-3xl mx-auto" {...fadeInUp} data-id="element-112">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center" data-id="element-113">
              <p className="text-white/80 leading-relaxed" data-id="element-114">
                DFB metodika dalinasi į žvalgą: kiekvienai veiklai reikalinga
                skirtinga psichikos būsena. Trumpų distancijų bėgimas reikalauja
                kitokio pasirengimo nei maratonas. Lengvas pokalbis – tai viena
                būsena, o intensyvus protinis darbas – visai kita.
              </p>
            </div>
          </motion.div>

          {/* Vertical Timeline Flow */}
          <motion.div className="relative max-w-4xl mx-auto py-8" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-115">
            {/* Central Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-12 bottom-12 w-0.5 bg-gradient-to-b from-gold/60 via-gold/40 to-gold/20 -translate-x-1/2" data-id="element-116" />

            <div className="flex flex-col gap-16 md:gap-20 relative z-10" data-id="element-117">
              {[{
              title: '4 Mentalinio Aktyvumo Būsenos',
              desc: 'Nuo chaoso iki tikslaus mąstymo',
              img: 'https://cdn.magicpatterns.com/uploads/86D17L5NgjqReE3GCNF7d3/Screenshot_2026-03-06_at_13.44.55.png',
              imgStyle: {
                backgroundSize: '250%',
                backgroundPosition: '55% 35%'
              } as React.CSSProperties
            }, {
              title: '4 Emocinio Aktyvumo Būsenos',
              desc: 'Nuo gilumo ir jautrumo iki lengvumo ir dinamiškumo bendraujant',
              img: 'https://cdn.magicpatterns.com/uploads/oYRvzX8HGPdzN3RmPEhdeg/zac-durant-_6HzPU9Hyfg-unsplash.jpg',
              imgStyle: {} as React.CSSProperties
            }, {
              title: '4 Fizinio Aktyvumo Būsenos',
              desc: 'Nuo plaučių, erdivinių judėsių iki tikslingo, koordinuotos veiklos',
              img: 'https://cdn.magicpatterns.com/uploads/6RbKwQ7msGyRimZEELWpys/atharva-tulsi-2jA_YoQuvaA-unsplash.jpg',
              imgStyle: {} as React.CSSProperties
            }, {
              title: '4 Harmoningo Aktyvumo Būsenos',
              desc: 'Kūnas, emocijos ir protas veikia darniai kaip viena visuma',
              img: 'https://cdn.magicpatterns.com/uploads/deWv2jrD7JtPFbuwEysfBb/IMG_7871.jpg',
              imgStyle: {
                backgroundSize: '180%',
                backgroundPosition: '50% 40%'
              } as React.CSSProperties
            }].map((item, idx) => {
              const isEven = idx % 2 === 0;
              return <motion.div key={idx} variants={fadeInUp} className={`relative flex flex-col md:flex-row items-start md:items-center w-full ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-118">
                    {/* Mobile Dot */}
                    <div className="md:hidden absolute left-8 top-12 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-119" />

                    {/* Desktop Center Dot */}
                    <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-120" />

                    {/* Image Side */}
                    <div className={`w-full md:w-1/2 flex pl-20 md:pl-0 ${isEven ? 'md:justify-end md:pr-16' : 'md:justify-start md:pl-16'} mb-5 md:mb-0`} data-id="element-121">
                      <div className="relative z-10 w-24 h-24 rounded-full overflow-hidden border-4 border-gold ring-2 ring-gold/25 flex-shrink-0 shadow-xl" data-id="element-122">
                        <div className="w-full h-full bg-cover bg-center" style={{
                      backgroundImage: `url('${item.img}')`,
                      ...item.imgStyle
                    }} data-id="element-123" />
                      </div>
                    </div>

                    {/* Text Side */}
                    <div className={`w-full md:w-1/2 flex flex-col pl-20 md:pl-0 ${isEven ? 'md:items-start md:pl-16' : 'md:items-end md:pr-16 md:text-right'}`} data-id="element-124">
                      <h4 className="font-bold text-white mb-2 text-[20px]" data-id="element-125">
                        {item.title}
                      </h4>
                      <p className="text-sm text-white/60 leading-relaxed max-w-sm" data-id="element-126">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-127">
        <div className="max-w-5xl mx-auto" data-id="element-128">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-129">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-130">
              Rezultatai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-131" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-132">
              DFB Metodologijos Nauda
            </h2>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-133">
            {/* First row: 3 items */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 mb-12" data-id="element-134">
              {[{
              icon: <HeartIcon className="w-12 h-12 text-gold-dark" data-id="element-135" />,
              title: 'Nuolatinė Ramybė',
              desc: 'Stabilumas nepriklausomai nuo išorinio pasaulio'
            }, {
              icon: <BrainIcon className="w-12 h-12 text-gold-dark" data-id="element-136" />,
              title: 'Dėmesio Valdymas',
              desc: 'Sąmoningas dėmesio nukreipimas į svarbius dalykus'
            }, {
              icon: <SparklesIcon className="w-12 h-12 text-gold-dark" data-id="element-137" />,
              title: 'Aktyvi Savireguliacija',
              desc: 'Efektyvus streso ir emocijų valdymas'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-138">
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-139" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-140">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-141">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>

            {/* Second row: 2 items centered */}
            <div className="flex flex-col sm:flex-row justify-center gap-12" data-id="element-142">
              {[{
              icon: <Users className="w-12 h-12 text-gold-dark" data-id="element-143" />,
              title: 'Emocinis Intelektas',
              desc: 'Geresnis savęs ir kitų supratimas'
            }, {
              icon: <SunIcon className="w-12 h-12 text-gold-dark" data-id="element-144" />,
              title: 'Giluminis Poilsis',
              desc: 'Efektyvus atsigavimas per trumpą laiką'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center sm:w-[calc(33.333%-1.5rem)]" data-id="element-145">
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-146" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-147">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-148">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE OFFER ── (dark bg) */}
      <section id="registracija" className="py-24 px-4 bg-mountain-darker text-white" data-id="element-149">
        <div className="max-w-5xl mx-auto" data-id="element-150">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-151">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-152">
              DFB Metodika: Medituoti Lengva
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-153" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-154">
              Prisijunk Prie Kurso
            </h2>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row items-stretch justify-center mb-12" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-155">
            {[{
            value: '90',
            label: 'Akademinių valandų',
            sub: '30 užsiėmimų po 2–2.5 val.'
          }, {
            value: '2.5',
            label: 'Mėnesiai',
            sub: 'Programos trukmė'
          }, {
            value: '10',
            label: 'Dalyvių max',
            sub: 'Optimali grupė'
          }, {
            value: '330€',
            label: '110€ už mėnesį',
            sub: 'Galimybė mokėti dalimis'
          }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-156">
                {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
              width: 1,
              height: '60%',
              background: 'rgba(255,255,255,0.12)'
            }} data-id="element-157" />}
                <span className="font-bold text-gold-light leading-none mb-3" style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
            }} data-id="element-158">
                  {stat.value}
                </span>
                <p className="text-sm font-semibold text-white uppercase tracking-wide mb-1" data-id="element-159">
                  {stat.label}
                </p>
                <p className="text-xs text-white/50 leading-relaxed" data-id="element-160">
                  {stat.sub}
                </p>
              </motion.div>)}
          </motion.div>

          <div className="w-full h-px mb-12" style={{
          background: 'rgba(255,255,255,0.1)'
        }} data-id="element-161" />

          <motion.div className="text-center" {...fadeInUp} data-id="element-162">
            <motion.a href="https://ig.me/m/vestirna" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05,
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2.5 whitespace-nowrap text-white px-10 py-[15px] rounded-full text-base font-semibold shadow-2xl transition-colors mb-5" style={{
            background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
          }} data-id="element-163">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-164">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-165" />
              </svg>
              Parašyk mums žinutę
            </motion.a>
            <div className="flex items-center justify-center gap-2 mt-3" data-id="element-166">
              <p className="text-sm text-white/50" data-id="element-167">
                Įdomu? Turi klausimų? Tiesiog parašyk ✨
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE STRUCTURE ── (light bg) */}
      <section id="programa" className="py-24 px-4 bg-sand-light" data-id="element-168">
        <div className="max-w-6xl mx-auto" data-id="element-169">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-170">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-171">
              3 etapai: išmok, patirk, pritaikyk
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-172" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-173">
              Kurso Struktūra
            </h2>
          </motion.div>

          <motion.div className="space-y-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-174">
            {[{
            num: '01',
            title: 'Pirmoji Dalis: Pagrindai',
            desc: 'Baziniai pratimai ir teoriniai pagrindai',
            img: 'https://images.unsplash.com/photo-1712342109846-a8fcb1c883ba?w=1200&q=80'
          }, {
            num: '02',
            title: 'Antroji Dalis: Būsenos',
            desc: '16 aktyvumo būsenų atpažinimas ir valdymas',
            img: 'https://images.unsplash.com/photo-1703945530393-600961291ced?w=1200&q=80'
          }, {
            num: '03',
            title: 'Trečioji Dalis: Integracija',
            desc: 'Įgūdžių taikymas kasdieniame gyvenime',
            img: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1200&q=80'
          }].map(part => <motion.div key={part.num} variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 200
          }} data-id="element-175">
                {/* Background image */}
                <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url('${part.img}')`
            }} data-id="element-176" />
                {/* Left-to-right dark gradient */}
                <div className="absolute inset-0" style={{
              background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.1) 100%)'
            }} data-id="element-177" />
                {/* Ghost number — far right */}
                <div className="absolute right-8 top-1/2 -translate-y-1/2 font-bold select-none leading-none text-white" style={{
              fontSize: '8rem',
              opacity: 0.1
            }} aria-hidden="true" data-id="element-178">
                  {part.num}
                </div>
                {/* Content — left side */}
                <div className="relative z-10 flex items-center gap-6 px-10 py-10" data-id="element-179">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/60 flex items-center justify-center" data-id="element-180">
                    <span className="text-gold-light font-bold text-sm tracking-widest" data-id="element-181">
                      {part.num}
                    </span>
                  </div>
                  <div data-id="element-182">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 leading-tight" data-id="element-183">
                      {part.title}
                    </h3>
                    <p className="text-white/70 text-base md:text-lg leading-relaxed" data-id="element-184">
                      {part.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest overflow-hidden" data-id="element-185">
        <div className="max-w-6xl mx-auto" data-id="element-186">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-187">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-188">
              Tikros transformacijos istorijos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-189" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-190">
              Dalyvių Atsiliepimai
            </h2>
          </motion.div>

          {(() => {
          const testimonials = [{
            name: 'Eglė V.',
            role: 'Kursų dalyvė',
            quote: 'Nuo pirmos dienos jaučiau, kad patekau į saugią ir gerai apgalvotą mokymosi struktūrą, bet kartu apsupta labai daug šilumos ir palaikymo. DFB padėjo man atpažinti ir naviguoti neramius jausmus ir chaotiškas būsenas, bei juos keisti. Sugrįžti į čia ir dabar. Prisiimti atsakomybę už savo dėmesį. Nuosekliai kartojant pratimus ir darant praktikas įvyko pozityvus pokytis.'
          }, {
            name: 'Ieva M.',
            role: 'Kursų dalyvė',
            quote: 'Labiausiai patiko, kad 99% mokymų buvo patyrimas. Suvokimas visko per save. Būtent todėl DFB lengvai integravosi į mano gyvenimą. Perskaičiau ne vieną aprašymą apie DFB internete - visiškai neatspindi to, ką gauni. O gauni be galo daug. Sakyčiau, kad DFB - didelis asmeninis virsmas.'
          }, {
            name: 'Mantas R.',
            role: 'Kursų dalyvis',
            quote: 'Pagrindiniai įsisamoninimai ir suvokimai atėję nuoširdžiai praktikuojant ir naudojant DFB metodą yra: išmokimas, kaip keisti savo būsenas ir stebėti kaip per tai keičiasi ir aplinka, žmonės bei jų reakcijos; suvokimas, kaip įgalinti savo naudai visas būsenas neskirstant jų į geras ir blogas.'
          }, {
            name: 'Dovydas S.',
            role: 'Kursų dalyvis',
            quote: 'Labai aiškiai ir struktūruotai perteikiama kurso informacija, palydima per patyrimus. Isiklausymas į tai, kas kiekvienam aktualu, koks kiekvieno mokinio iššūkis, ir atsižvelgimas į tai. Rūpestis erdve, atmosfera, lankstumas situacijoms. Nuolatinis palaikymas, domėjimasis, įkvėpimas per konkrečius pavyzdžius. Dėkui už transformaciją ir nepakartojama pokytį.'
          }];
          const goToTestimonial = (idx: number) => {
            setTestimonialDirection(idx > activeTestimonial ? 1 : -1);
            setActiveTestimonial(idx);
          };
          const prevTestimonial = () => goToTestimonial(activeTestimonial === 0 ? testimonials.length - 1 : activeTestimonial - 1);
          const nextTestimonial = () => goToTestimonial(activeTestimonial === testimonials.length - 1 ? 0 : activeTestimonial + 1);
          const currentTestimonial = testimonials[activeTestimonial];
          const slideVariants = {
            enter: (dir: number) => ({
              opacity: 0,
              x: dir * 60
            }),
            center: {
              opacity: 1,
              x: 0
            },
            exit: (dir: number) => ({
              opacity: 0,
              x: dir * -60
            })
          };
          return <div className="relative flex flex-col items-center" data-id="element-191">
                <div className="flex items-center w-full gap-4 md:gap-8" data-id="element-192">
                  {/* Left arrow */}
                  <button onClick={prevTestimonial} aria-label="Ankstesnis" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-193">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-194">
                      <polyline points="15 18 9 12 15 6" data-id="element-195" />
                    </svg>
                  </button>

                  {/* Testimonial content */}
                  <div className="flex-1 min-w-0 overflow-hidden" data-id="element-196">
                    <AnimatePresence mode="wait" custom={testimonialDirection} data-id="element-197">
                      <motion.div key={activeTestimonial} custom={testimonialDirection} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.45,
                    ease: 'easeInOut'
                  }} className="flex flex-col items-center text-center px-2 md:px-8" data-id="element-198">
                        {/* Decorative quote mark */}
                        <span className="font-serif text-gold/25 select-none leading-none mb-2" style={{
                      fontSize: '6rem',
                      lineHeight: 0.8
                    }} aria-hidden="true" data-id="element-199">
                          &ldquo;
                        </span>

                        {/* Quote text — dynamically from current testimonial */}
                        <p className="text-mountain-darker text-lg md:text-xl leading-relaxed whitespace-pre-line max-w-2xl mx-auto mb-10 font-[400]" data-id="element-200">
                          {currentTestimonial.quote}
                        </p>

                        {/* Gold divider */}
                        <div className="w-12 h-px bg-gold mb-4" data-id="element-201" />

                        {/* Name & role */}
                        <p className="font-bold text-mountain-darker text-lg" data-id="element-202">
                          {currentTestimonial.name}
                        </p>
                        <p className="text-sm text-mountain-dark/60 mt-0.5" data-id="element-203">
                          {currentTestimonial.role}
                        </p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Right arrow */}
                  <button onClick={nextTestimonial} aria-label="Kitas" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-204">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-205">
                      <polyline points="9 18 15 12 9 6" data-id="element-206" />
                    </svg>
                  </button>
                </div>

                {/* Dot indicators */}
                <div className="flex items-center gap-3 mt-10" data-id="element-207">
                  {testimonials.map((_, i) => <button key={i} onClick={() => goToTestimonial(i)} aria-label={`Atsiliepimas ${i + 1}`} className="transition-all duration-300 rounded-full" style={{
                width: i === activeTestimonial ? '2rem' : '0.5rem',
                height: '0.5rem',
                background: i === activeTestimonial ? 'rgb(var(--gold))' : 'rgba(var(--gold), 0.3)'
              }} data-id="element-208" />)}
                </div>
              </div>;
        })()}
        </div>
      </section>

      {/* ── TEAM ── (dark bg) */}
      <section id="komanda" className="py-32 px-4 bg-mountain-darker text-white" data-id="element-209">
        <div className="max-w-4xl mx-auto" data-id="element-210">
          <motion.div className="text-center mb-20" {...fadeInUp} data-id="element-211">
            <p className="font-accent italic text-gold-light mb-4 text-xl" data-id="element-212">
              Patyrę instruktoriai ir praktikai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-213" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-214">
              Mūsų Komanda
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-215">
            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-216">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-217">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/p5KUhhvxDrMn2D53eKDHKt/agne3.jpg')",
                backgroundPosition: 'center 30%'
              }} data-id="element-218" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-219">
                Agnė Kubertė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-220">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-221" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-222">
                DFB metodikos sertifikuota instruktorė, daugiau nei 10 metų
                meditacijų praktikė. Mokymuose ji padeda žmonėms sujungti
                meditaciją su kasdieniu gyvenimu – įnešti daugiau aiškumo,
                sąmoningo buvimo ir vidinės ramybės. Padeda dalyviams išmokti
                savireguliacijos ir lengvai pritaikyti meditacijos principus
                realiose gyvenimo situacijose.
              </p>
              <div className="relative max-w-xs" data-id="element-223">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-224">
                  "
                </span>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-225">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-226">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/cYk35xR7d8THHiA5ofpAZk/Screenshot_2026-03-06_at_13.56.34.png')",
                backgroundPosition: 'center 40%'
              }} data-id="element-227" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-228">
                Vestina Zablockytė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-229">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-230" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-231">
                „Sąmoningas judėjimas, šokis, rebefingas, Vipasana... Per 15
                metų leidausi į daugybę savęs pažinimo kelionių ir išbandžiau
                pačias įvairiausias praktikas. Tačiau būtent DFB metodika, kuria
                aktyviai gyvenu pastaruosius šešerius metus, tapo ta praktika,
                kuri iš esmės pakeitė mano kasdienybės kokybę. Šiandien noriu
                pasidalinti atradimu, kad medituoti lengva! Vietoj ilgų paieškų
                kviečiu iškart nerti į procesą, kuris atveria neįtikėtinai platų
                galimybių spektrą. Prisijunk!“
              </p>
              <div className="relative max-w-xs" data-id="element-232">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-233">
                  "
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SCHEDULE ── (light bg) */}
      <section id="tvarkarastis" className="py-24 px-4 bg-sand-lightest" data-id="element-234">
        <div className="max-w-6xl mx-auto" data-id="element-235">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-236">
            <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-237">
              Prisijunk prie pavasario grupės
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-238" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-239">
              Artėjantis Kursas
            </h2>
          </motion.div>

          <motion.div {...fadeInUp} className="space-y-10" data-id="element-240">
            {/* Date + days — unified card */}
            <div className="bg-white border border-sand-dark/20 rounded-2xl px-8 py-6 max-w-2xl mx-auto" data-id="element-241">
              <div className="flex items-center gap-3 justify-center mb-5" data-id="element-242">
                <CalendarIcon className="w-5 h-5 text-gold-dark flex-shrink-0" data-id="element-243" />
                <p className="text-2xl font-bold text-mountain-darker" data-id="element-244">
                  Kovo 16 – Gegužės 21
                </p>
              </div>
              <div className="w-12 h-px bg-gold mx-auto mb-5" data-id="element-245" />
              <div className="flex flex-wrap justify-center gap-2" data-id="element-246">
                {['Pirmadienis', 'Antradienis', 'Ketvirtadienis'].map(day => <div key={day} className="flex items-center gap-2 bg-sand-lightest border border-sand-dark/20 px-4 py-2 rounded-xl" data-id="element-247">
                    <div className="w-1.5 h-1.5 bg-gold rounded-full" data-id="element-248" />
                    <span className="text-mountain-darker font-medium text-sm" data-id="element-249">
                      {day}
                    </span>
                  </div>)}
              </div>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
            once: true,
            margin: '-80px'
          }} data-id="element-250">
              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-251">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-252">
                  Kovas 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-253">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-254">
                      {d}
                    </div>)}
                  {[...Array(6)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-255" />)}
                  {[...Array(31)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 6) % 7;
                  const active = (dow === 0 || dow === 1 || dow === 3) && day >= 16;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-256">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-257">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-258">
                  Balandis 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-259">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-260">
                      {d}
                    </div>)}
                  {[...Array(2)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-261" />)}
                  {[...Array(30)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 2) % 7;
                  const active = dow === 0 || dow === 1 || dow === 3;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-262">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-263">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-264">
                  Gegužė 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-265">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-266">
                      {d}
                    </div>)}
                  {[...Array(4)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-267" />)}
                  {[...Array(31)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 4) % 7;
                  const active = (dow === 0 || dow === 1 || dow === 3) && day <= 21;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-268">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>
            </motion.div>

            <motion.div className="flex flex-col sm:flex-row items-stretch justify-center border border-sand-dark/30 rounded-2xl bg-white overflow-hidden" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
            once: true,
            margin: '-80px'
          }} data-id="element-269">
              {[{
              value: '10',
              label: 'Savaičių',
              sub: 'Programos trukmė'
            }, {
              value: '30',
              label: 'Užsiėmimų',
              sub: 'Iš viso sesijų'
            }, {
              value: '90',
              label: 'Akademinių val.',
              sub: 'Po 2–2.5 val.'
            }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-270">
                  {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
                width: 1,
                height: '55%',
                background: 'rgb(var(--sand-dark) / 0.4)'
              }} data-id="element-271" />}
                  <span className="font-bold text-gold-dark leading-none mb-2" style={{
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
              }} data-id="element-272">
                    {stat.value}
                  </span>
                  <p className="text-sm font-semibold text-mountain-darker uppercase tracking-wide mb-1" data-id="element-273">
                    {stat.label}
                  </p>
                  <p className="text-xs text-mountain-dark/50" data-id="element-274">{stat.sub}</p>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA CHAT BUBBLE ── */}
      <section className="py-16 px-4 bg-sand-lightest" data-id="element-275">
        <motion.div className="relative max-w-6xl mx-auto" {...fadeInUp} data-id="element-276">
          {/* Card */}
          <div className="relative bg-mountain-darker rounded-3xl p-12 md:p-16 text-center flex flex-col items-center" data-id="element-277">
            {/* Chat icon */}
            <svg className="w-12 h-12 text-gold-light mb-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-278">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" data-id="element-279" />
            </svg>

            {/* Accent */}
            <p className="font-accent italic text-gold-light mb-3 text-[28px]" data-id="element-280">
              Įdomu?
            </p>

            {/* Heading */}
            <h2 className="text-white font-bold text-3xl md:text-4xl mb-4 leading-tight" data-id="element-281">
              Tiesiog Parašyk Mums Žinutę
            </h2>

            {/* Body */}
            <p className="text-white/70 mb-10 max-w-lg leading-relaxed" data-id="element-282">
              Atsakysime į visus tavo klausimus apie kursą, metodiką ir kaip
              prisijungti.
            </p>

            {/* Two buttons side by side */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-283">
              {/* Instagram button */}
              <motion.a href="https://ig.me/m/vestirna" target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.97
            }} className="inline-flex items-center gap-3 text-white px-8 py-4 rounded-full text-base font-semibold shadow-lg transition-all min-w-[240px] justify-center" style={{
              background: 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)'
            }} data-id="element-284">
                <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-id="element-285">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-286" />
                </svg>
                Instagram žinutė
              </motion.a>

              {/* Facebook button */}
              <motion.a href="https://www.facebook.com/vestina.zablockyte" target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.97
            }} className="inline-flex items-center gap-3 bg-white hover:bg-gray-50 text-mountain-darker px-8 py-4 rounded-full text-base font-semibold shadow-lg transition-all min-w-[240px] justify-center" data-id="element-287">
                <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-288">
                  <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.908 1.434 5.503 3.678 7.2V22l3.455-1.896A10.7 10.7 0 0012 20.486c5.523 0 10-4.145 10-9.243S17.523 2 12 2z" fill="#0084FF" data-id="element-289" />
                  <path d="M13.119 14.159L10.7 11.6l-4.7 2.6 5.166-5.484 2.482 2.559 4.636-2.559-5.165 5.443z" fill="white" data-id="element-290" />
                </svg>
                Facebook žinutė
              </motion.a>
            </div>
          </div>

          {/* Speech bubble tail */}
          <div className="flex justify-center" data-id="element-291">
            <div style={{
            width: 0,
            height: 0,
            borderLeft: '16px solid transparent',
            borderRight: '16px solid transparent',
            borderTop: '16px solid rgb(var(--mountain-darker))'
          }} data-id="element-292" />
          </div>
        </motion.div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-12 px-4 bg-sand-lightest border-t border-sand-dark/20" data-id="element-293">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-294">
          <div className="flex items-center gap-2" data-id="element-295">
            <span className="text-gold-dark font-accent text-xl tracking-wide font-[500]" data-id="element-296">
              medituoti lengva
            </span>
          </div>
          <p className="text-mountain-dark/50 text-sm" data-id="element-297">
            © 2026 medituoti lengva. Visos teisės saugomos.
          </p>
          <div className="flex items-center gap-6" data-id="element-298">
            <a href="#apie" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-299">
              Apie
            </a>
            <a href="#metodika" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-300">
              Metodika
            </a>
            <a href="#registracija" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-301">
              Registracija
            </a>
          </div>
        </div>
      </footer>
    </main>;
}